/**
 * Firm Success — the all-firms lists (#676; R1–R6, R18).
 *
 * Two lists behind a capacity toggle: Originating firms (last lead sent,
 * 7d/30d sends by arrival, today included) and Handling firms (under review
 * now = Decision Sent or Reviewing, assigned and not sent, placed now — live
 * placement counts). LegalFlare's own record is in neither (#1268). Rows click through to the
 * firm's page. Sorts are selectable modes, not a cascade (R1–R3): OF opens
 * on 7-day sends, HF on currently-under-review.
 *
 * Hidden (R5/R6): per-list, display-only (hidden firms stay in every
 * computation), server-side per login. Dormant (R18): registered firms
 * with no activity at all in that capacity — out of the main list,
 * findable here, rejoin automatically on their next case.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { FsListsResponse } from "@command-center/shared";
import { ClientSuccessNav } from "@/components/client-success-nav";
import {
  useFirmSuccessLists,
  useSetFirmHidden,
} from "@/lib/use-firm-success";
import { QCard, fmtDate } from "./fs-blocks";
import "./fs.css";

/** ISO day arithmetic, no timezone (the payload's dates are already PT days). */
function addDays(iso: string, n: number): string {
  const t = Date.parse(`${iso}T12:00:00Z`) + n * 86_400_000;
  // day-boundary: date arithmetic on a date-only label; no clock
  return new Date(t).toISOString().slice(0, 10);
}
/** computedThrough is yesterday; today is the day after it. */
function addDay(iso: string): string {
  return addDays(iso, 1);
}

type OfSort = "d7" | "d30" | "az";
type HfSort = "ur" | "placed" | "az";

export function FirmSuccessPage() {
  const lists = useFirmSuccessLists();

  // Both lists stack on ONE page, each with its own independent sort — the
  // ratified design. The build had put them behind a capacity toggle, which
  // nothing ruled (R4's toggle is for a both-capacity FIRM's page) and which
  // cost the thing the page is for: scanning the whole board at once.
  return (
    <div className="p-6">
      <ClientSuccessNav active="firm-success" />
      <div className="fs-surface" style={{ margin: "-8px -24px 0", borderRadius: 12 }}>
        <div className="shell">
          <h1>Firm Success</h1>
          <p className="sub" data-testid="fs-sub">
            Click a firm to open its page. Hiding a firm only hides the row — it stays in every
            number and comparison.
          </p>
          {lists.isLoading ? <p className="soft">Loading…</p> : null}
          {lists.isError ? <p className="soft">Could not load the lists.</p> : null}
          {lists.data !== undefined ? (
            <>
              <OfList data={lists.data} />
              <HfList data={lists.data} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** "3 days ago" beats "Jul 25" for a column you scan for gaps (F28/F29). */
function ago(iso: string | null, today: string): string {
  if (iso === null) return "—";
  const n = Math.round(
    (Date.parse(`${today}T12:00:00Z`) - Date.parse(`${iso.slice(0, 10)}T12:00:00Z`)) / 86_400_000,
  );
  if (n <= 0) return "today";
  return n === 1 ? "1 day ago" : `${n} days ago`;
}

function HideButton({
  capacity,
  firmId,
  hidden,
}: {
  capacity: "of" | "hf";
  firmId: string;
  hidden: boolean;
}) {
  const setHidden = useSetFirmHidden();
  return (
    <button
      type="button"
      className="hidebtn"
      onClick={(e) => {
        e.stopPropagation();
        setHidden.mutate({ capacity, firmId, hidden: !hidden });
      }}
      data-testid={`fs-hide-${firmId}`}
    >
      {hidden ? "Unhide" : "Hide"}
    </button>
  );
}

function ListFooters({
  capacity,
  hiddenRows,
  dormant,
  onOpen,
}: {
  capacity: "of" | "hf";
  hiddenRows: { firmId: string; name: string }[];
  dormant: { firmId: string; name: string }[];
  onOpen: (firmId: string) => void;
}) {
  const [showHidden, setShowHidden] = useState(false);
  const [showDormant, setShowDormant] = useState(false);
  const setHidden = useSetFirmHidden();
  return (
    <>
      <div className="hiddenbar">
        {hiddenRows.length > 0 ? (
          <button type="button" onClick={() => setShowHidden(!showHidden)} data-testid="fs-hidden-bar">
            Hidden ({hiddenRows.length}) {showHidden ? "▴" : "▾"}
          </button>
        ) : null}
        {dormant.length > 0 ? (
          <button type="button" onClick={() => setShowDormant(!showDormant)} data-testid="fs-dormant-bar">
            Dormant ({dormant.length}) {showDormant ? "▴" : "▾"}
          </button>
        ) : null}
        {showHidden && hiddenRows.length > 0 ? (
          <div className="hiddenrows">
            <table className="list">
              <tbody>
                {hiddenRows.map((r) => (
                  <tr key={r.firmId} onClick={() => onOpen(r.firmId)}>
                    <td className="firm">{r.name}</td>
                    <td className="r">
                      <button
                        type="button"
                        className="hidebtn"
                        style={{ visibility: "visible" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHidden.mutate({ capacity, firmId: r.firmId, hidden: false });
                        }}
                      >
                        Unhide
                      </button>
                    </td>
                  </tr>
                ))}
                {hiddenRows.length > 1 ? (
                  <tr>
                    <td colSpan={2} className="r">
                      <button
                        type="button"
                        className="hidebtn"
                        style={{ visibility: "visible" }}
                        onClick={() => {
                          for (const r of hiddenRows) {
                            setHidden.mutate({ capacity, firmId: r.firmId, hidden: false });
                          }
                        }}
                      >
                        Unhide all
                      </button>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
        {showDormant && dormant.length > 0 ? (
          <div className="hiddenrows">
            <table className="list">
              <tbody>
                {dormant.map((r) => (
                  <tr key={r.firmId} onClick={() => onOpen(r.firmId)}>
                    <td className="firm">{r.name}</td>
                    <td className="r soft" style={{ fontSize: 12 }}>
                      {capacity === "of" ? "never sent us a case" : "never received a case"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="note">
              Registered firms with no activity in this capacity, ever. The lists build from
              activity, so a dormant firm joins the list automatically the moment it sends or
              receives a case.
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

function OfList({ data }: { data: FsListsResponse }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<OfSort>("d7");
  const hiddenSet = new Set(data.hiddenOf);
  const az = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const rows = [...data.of]
    .filter((r) => !hiddenSet.has(r.firmId))
    .sort(
      sort === "az"
        ? az
        : (a, b) =>
            (sort === "d7" ? b.sends7d - a.sends7d : b.sends30d - a.sends30d) || az(a, b),
    );
  const hiddenRows = data.of.filter((r) => hiddenSet.has(r.firmId));
  const open = (firmId: string) =>
    void navigate({ to: "/firm-success/firm-success/$firmId", params: { firmId } });

  const today = addDay(data.computedThrough);
  return (
    <QCard
      title="Originating firms: has anyone gone quiet?"
      accent="var(--q-send)"
      right={
        <span className="sorts" data-testid="fs-of-sorts">
          <button type="button" aria-pressed={sort === "d7"} onClick={() => setSort("d7")}>
            Sent, last 7 days
          </button>
          <button type="button" aria-pressed={sort === "d30"} onClick={() => setSort("d30")}>
            Sent, last 30 days
          </button>
          <button type="button" aria-pressed={sort === "az"} onClick={() => setSort("az")}>
            A–Z
          </button>
        </span>
      }
      win={
        <>
          {data.of.length} firms have sent leads; LegalFlare's own record is not listed. "Last 7
          days" = {fmtDate(addDays(today, -6))} – today; "last 30 days" ={" "}
          {fmtDate(addDays(today, -29))} – today, today included, counted live by the day each
          lead reached us. Lead Volume's "Last 7 days" ends yesterday and counts each lead on its
          Creation Date, so the two can differ.
        </>
      }
    >
      <table className="list" data-testid="fs-of-list">
        <thead>
          <tr>
            <th>Firm</th>
            <th className="r">Last lead sent</th>
            <th className="r">Leads, last 7 days</th>
            <th className="r">Leads, last 30 days</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.firmId} onClick={() => open(r.firmId)} data-testid={`fs-of-row-${r.firmId}`}>
              <td className="firm">{r.name}</td>
              <td className="r num">
                {ago(r.lastLeadSent, today)}
                {/* Estimates are marked, never hidden. For rows a write batch
                    moved (provenance migration_2026_04, signed_leads_fold_2026_05:
                    their batches[]), arrival is the batch's moment and this
                    falls back to the matter's real Creation Date (#680). */}
                {r.lastLeadSentEstimated && (
                  <span className="fs-est">
                    {" "}
                    est.
                  </span>
                )}
              </td>
              <td className="r num">{r.sends7d}</td>
              <td className="r num">{r.sends30d}</td>
              <td className="r">
                <HideButton capacity="of" firmId={r.firmId} hidden={false} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ListFooters capacity="of" hiddenRows={hiddenRows} dormant={data.dormantOf} onOpen={open} />
      <div className="note">
        Whether a gap is unusual depends on the firm; open its page to see its own sending
        pattern.
      </div>
    </QCard>
  );
}

function HfList({ data }: { data: FsListsResponse }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<HfSort>("ur");
  const hiddenSet = new Set(data.hiddenHf);
  const az = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const rows = [...data.hf]
    .filter((r) => !hiddenSet.has(r.firmId))
    .sort(
      sort === "az"
        ? az
        : (a, b) =>
            (sort === "ur"
              ? b.underReviewNow - a.underReviewNow
              : b.placedNow - a.placedNow) || az(a, b),
    );
  const hiddenRows = data.hf.filter((r) => hiddenSet.has(r.firmId));
  const open = (firmId: string) =>
    void navigate({ to: "/firm-success/firm-success/$firmId", params: { firmId } });

  const today = addDay(data.computedThrough);
  return (
    <QCard
      title="Handling firms: what are they holding?"
      accent="var(--q-resp)"
      right={
        <span className="sorts" data-testid="fs-hf-sorts">
          <button type="button" aria-pressed={sort === "ur"} onClick={() => setSort("ur")}>
            Under review now
          </button>
          <button type="button" aria-pressed={sort === "placed"} onClick={() => setSort("placed")}>
            Placed with them now
          </button>
          <button type="button" aria-pressed={sort === "az"} onClick={() => setSort("az")}>
            A–Z
          </button>
        </span>
      }
      win={
        <>
          {data.hf.length} firms hold a Placement with us. "Under review now" = we sent it and
          they have not yet accepted or declined (Decision Sent or Reviewing). "Assigned, not
          sent" = at Decision Assigned and not sent to them; Lead Sent sends only to a firm that
          is Active.
        </>
      }
    >
      <table className="list" data-testid="fs-hf-list">
        <thead>
          <tr>
            <th>Firm</th>
            <th className="r">Under review now</th>
            <th className="r">Assigned, not sent</th>
            <th className="r">Placed with them now</th>
            <th className="r">Last lead we sent them</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.firmId} onClick={() => open(r.firmId)} data-testid={`fs-hf-row-${r.firmId}`}>
              <td className="firm">{r.name}</td>
              <td className="r num">{r.underReviewNow}</td>
              <td className="r num">{r.assignedNotSent}</td>
              <td className="r num">{r.placedNow}</td>
              <td className="r num">{ago(r.lastSentTo, today)}</td>
              <td className="r">
                <HideButton capacity="hf" firmId={r.firmId} hidden={false} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ListFooters capacity="hf" hiddenRows={hiddenRows} dormant={data.dormantHf} onOpen={open} />
    </QCard>
  );
}
